import React from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { SITE_BASE_URL } from "./constants/urls";
const IMAGE_CDN_BASE_URL = process.env.REACT_APP_IMAGE_CDN_SEO_URL;
const PRODUCTION_HOSTNAME = new URL(SITE_BASE_URL).hostname;
const isNonProduction =
  typeof window !== "undefined" &&
  window.location.hostname !== PRODUCTION_HOSTNAME;
const SUPPORTED_LANGUAGES = ["en", "fr"];
const ROUTE_KEYS = ["home", "menu", "privacyPolicy", "termsOfUse"];

const normalizeLanguage = (language) =>
  String(language || "en").toLowerCase().startsWith("fr") ? "fr" : "en";

const getRouteTranslator = (i18n, language) =>
  i18n.getFixedT(normalizeLanguage(language), "routes");

const getSeoEntry = (i18n, language, seoKey) => {
  const seoEntry = i18n.getFixedT(normalizeLanguage(language), "seo")(seoKey, {
    returnObjects: true,
  });

  return typeof seoEntry === "object" && seoEntry !== null ? seoEntry : null;
};

const findRouteKeyByPath = (i18n, pathname, language) => {
  const languages = language
    ? [normalizeLanguage(language)]
    : SUPPORTED_LANGUAGES;

  for (const currentLanguage of languages) {
    const routeTranslator = getRouteTranslator(i18n, currentLanguage);
    const matchedRouteKey = ROUTE_KEYS.find(
      (routeKey) => routeTranslator(routeKey) === pathname
    );

    if (matchedRouteKey) {
      return matchedRouteKey;
    }
  }

  return null;
};

const getSeoImageUrl = (imagePath) => {
  if (!imagePath) {
    return "";
  }

  if (/^https?:\/\//i.test(imagePath)) {
    return imagePath;
  }

  const normalizedPath = imagePath.startsWith("/")
    ? imagePath
    : `/${imagePath}`;

  if (IMAGE_CDN_BASE_URL) {
    return `${IMAGE_CDN_BASE_URL}${normalizedPath}`;
  }

  if (typeof window !== "undefined") {
    return `${window.location.origin}${normalizedPath}`;
  }

  return normalizedPath;
};

const resolveSeoState = ({ i18nInstance, item, seoKeyName, pathname, language }) => {
  if (item) {
    const inferredRouteKey = findRouteKeyByPath(i18nInstance, pathname, language);
    return {
      seoItem: item,
      routeKey: seoKeyName || inferredRouteKey || "home",
    };
  }

  const normalizedLanguage = normalizeLanguage(language);
  const routeKeyFromPath = findRouteKeyByPath(
    i18nInstance,
    pathname,
    normalizedLanguage
  );
  const seoFromKeyName = seoKeyName
    ? getSeoEntry(i18nInstance, normalizedLanguage, seoKeyName)
    : null;
  const seoFromRoute = routeKeyFromPath
    ? getSeoEntry(i18nInstance, normalizedLanguage, routeKeyFromPath)
    : null;

  const matchedKeyFromName = seoFromKeyName ? seoKeyName : null;
  const matchedKeyFromRoute = seoFromRoute ? routeKeyFromPath : null;
  const resolvedRouteKey =
    matchedKeyFromName || matchedKeyFromRoute || "home";

  return {
    seoItem:
      seoFromKeyName ||
      seoFromRoute ||
      getSeoEntry(i18nInstance, normalizedLanguage, "home") ||
      getSeoEntry(i18nInstance, "en", "home"),
    routeKey: resolvedRouteKey,
  };
};

export const SEO = ({ item, seoKeyName, pathname }) => {
  const { i18n } = useTranslation();
  const location = useLocation();
  const resolvedPathname =
    pathname ||
    location?.pathname ||
    (typeof window !== "undefined" ? window.location.pathname : "");

  const { seoItem, routeKey } = resolveSeoState({
    i18nInstance: i18n,
    item,
    seoKeyName,
    pathname: resolvedPathname,
    language: i18n.language,
  });

  const enAlternates =
    getSeoEntry(i18n, "en", routeKey) || getSeoEntry(i18n, "en", "home");
  const frAlternates =
    getSeoEntry(i18n, "fr", routeKey) || getSeoEntry(i18n, "fr", "home");

  return (
    <Helmet>
      {isNonProduction && (
        <meta name="robots" content="noindex, nofollow" />
      )}
      <title>{seoItem.title}</title>
      <meta name="description" content={seoItem.description} />
      <meta property="og:type" content="webapp" />
      <meta property="og:title" content={seoItem.title} />
      {seoItem.image && (
        <meta property="og:image" content={getSeoImageUrl(seoItem.image)} />
      )}
      <meta property="og:description" content={seoItem.description} />
      {seoItem.keywords && (
        <meta property="og:keywords" content={seoItem.keywords} />
      )}
      {seoItem.url && <meta property="og:url" content={seoItem.url} />}

      {/* Add hreflang attributes for English and French versions */}
      {seoItem.url && (
        <>
          <link rel="alternate" hreflang="en" href={enAlternates.url} />
          <link rel="alternate" hreflang="fr" href={frAlternates.url} />
          <link rel="canonical" href={seoItem.url} />
        </>
      )}
    </Helmet>
  );
};